import { useEffect, useRef, useState } from "react";
import { useProject } from "./ProjectLayout";
import { useAuth } from "@/context/AuthContext";
import api, { BACKEND, getToken } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Upload, FileText, Download, Plus, Trash2, Paperclip, CheckCircle2, Circle, MinusCircle, Pencil } from "lucide-react";

const STATUS_META = {
  pending: { label: "Awaiting", tone: "bg-amber-50 text-amber-600 ring-amber-200", icon: Circle },
  provided: { label: "Provided", tone: "bg-emerald-50 text-emerald-600 ring-emerald-200", icon: CheckCircle2 },
  not_applicable: { label: "N/A", tone: "bg-slate-100 text-slate-500 ring-slate-200", icon: MinusCircle },
};

export default function Documents() {
  const { project } = useProject();
  const { user } = useAuth();
  const canManage = user?.role === "admin" || user?.role === "consultant";
  const [data, setData] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [edit, setEdit] = useState(null);

  const load = () => api.get(`/projects/${project.id}/doc-requirements`).then((r) => setData(r.data));
  useEffect(() => { load(); }, [project.id]);

  const download = (d) => {
    fetch(`${BACKEND}/api/documents/${d.id}/download`, { headers: { Authorization: `Bearer ${getToken()}` } })
      .then((r) => r.blob())
      .then((b) => { const u = URL.createObjectURL(b); window.open(u, "_blank"); });
  };

  const removeDoc = async (d) => {
    try { await api.delete(`/documents/${d.id}`); toast.success("File removed"); load(); }
    catch { toast.error("Could not remove file"); }
  };

  const removeRow = async (rid) => {
    try { await api.delete(`/projects/${project.id}/doc-requirements/${rid}`); toast.success("Row removed"); load(); }
    catch { toast.error("Could not remove row"); }
  };

  const setStatus = async (rid, status) => {
    try { await api.patch(`/projects/${project.id}/doc-requirements/${rid}`, { status }); load(); }
    catch { toast.error("Could not update status"); }
  };

  const provided = data?.requirements?.filter((r) => r.status === "provided").length || 0;
  const total = data?.requirements?.length || 0;

  return (
    <div className="px-6 py-8 lg:px-10">
      <div className="mx-auto max-w-5xl">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h2 className="font-heading text-xl font-bold text-slate-900">Supporting Documents</h2>
            <p className="mt-1 text-sm text-slate-500">The files we need from you. Attach a sample against each item — {provided} of {total} provided.</p>
          </div>
          {canManage && (
            <Dialog open={addOpen} onOpenChange={setAddOpen}>
              <DialogTrigger asChild>
                <Button data-testid="add-doc-requirement-btn" className="rounded-lg bg-sky-600 hover:bg-sky-700"><Plus className="mr-1.5 h-4 w-4" /> Add document</Button>
              </DialogTrigger>
              <RequirementDialog projectId={project.id} onSaved={() => { setAddOpen(false); load(); }} />
            </Dialog>
          )}
        </div>

        <div className="mt-6 overflow-hidden rounded-2xl border border-slate-200 bg-white">
          <div className="hidden grid-cols-12 gap-4 border-b border-slate-100 bg-slate-50 px-5 py-3 text-xs font-bold uppercase tracking-wide text-slate-400 md:grid">
            <div className="col-span-5">Document</div>
            <div className="col-span-2">Status</div>
            <div className="col-span-5">Attached samples</div>
          </div>
          {data?.requirements?.map((r) => {
            const st = STATUS_META[r.status] || STATUS_META.pending;
            return (
              <div key={r.id} data-testid={`doc-req-${r.id}`} className="grid grid-cols-1 gap-4 border-b border-slate-100 px-5 py-4 last:border-0 md:grid-cols-12 md:items-start">
                <div className="md:col-span-5">
                  <div className="font-heading text-sm font-bold text-slate-800">{r.label}</div>
                  {r.description && <div className="mt-0.5 text-xs text-slate-500">{r.description}</div>}
                  {canManage && (
                    <div className="mt-2 flex gap-2">
                      <button data-testid={`edit-req-${r.id}`} onClick={() => setEdit(r)} className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-sky-600"><Pencil className="h-3 w-3" /> Edit</button>
                      <button data-testid={`delete-req-${r.id}`} onClick={() => removeRow(r.id)} className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-red-500"><Trash2 className="h-3 w-3" /> Remove</button>
                    </div>
                  )}
                </div>
                <div className="md:col-span-2">
                  {canManage ? (
                    <Select value={r.status} onValueChange={(v) => setStatus(r.id, v)}>
                      <SelectTrigger data-testid={`req-status-${r.id}`} className="h-8 w-32 rounded-lg text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pending">Awaiting</SelectItem>
                        <SelectItem value="provided">Provided</SelectItem>
                        <SelectItem value="not_applicable">N/A</SelectItem>
                      </SelectContent>
                    </Select>
                  ) : (
                    <span className={"inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-bold ring-1 " + st.tone}><st.icon className="h-3.5 w-3.5" /> {st.label}</span>
                  )}
                </div>
                <div className="md:col-span-5">
                  <div className="space-y-2">
                    {(r.documents || []).map((d) => (
                      <div key={d.id} data-testid={`req-file-${d.id}`} className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50/60 px-3 py-2">
                        <FileText className="h-4 w-4 shrink-0 text-sky-600" />
                        <div className="min-w-0 flex-1"><div className="truncate text-sm font-medium text-slate-700">{d.original_filename}</div><div className="text-[11px] text-slate-400">{(d.size / 1024).toFixed(0)} KB · {d.uploaded_by}</div></div>
                        <button onClick={() => download(d)} className="rounded p-1.5 text-slate-400 hover:text-sky-600"><Download className="h-4 w-4" /></button>
                        <button onClick={() => removeDoc(d)} data-testid={`remove-file-${d.id}`} className="rounded p-1.5 text-slate-400 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
                      </div>
                    ))}
                    <AttachButton projectId={project.id} requirementId={r.id} onDone={load} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {data?.other_documents?.length > 0 && (
          <div className="mt-8">
            <h3 className="font-heading text-base font-bold text-slate-900">Other documents</h3>
            <p className="mt-1 text-sm text-slate-500">Files uploaded from module questions or not tied to a checklist item.</p>
            <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
              {data.other_documents.map((d) => (
                <div key={d.id} data-testid={`other-doc-${d.id}`} className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-sky-50"><FileText className="h-5 w-5 text-sky-600" /></div>
                  <div className="min-w-0 flex-1"><div className="truncate font-semibold text-slate-800">{d.original_filename}</div><div className="text-xs text-slate-400">{(d.size / 1024).toFixed(0)} KB</div></div>
                  <button onClick={() => download(d)} className="rounded-lg p-2 text-slate-400 hover:bg-slate-100 hover:text-sky-600"><Download className="h-4 w-4" /></button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {edit && (
        <Dialog open onOpenChange={(o) => !o && setEdit(null)}>
          <RequirementDialog projectId={project.id} requirement={edit} onSaved={() => { setEdit(null); load(); }} />
        </Dialog>
      )}
    </div>
  );
}

function AttachButton({ projectId, requirementId, onDone }) {
  const inputRef = useRef();
  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("requirement_id", requirementId);
        await api.post(`/projects/${projectId}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success(files.length > 1 ? `${files.length} files attached` : "File attached");
      onDone();
    } catch { toast.error("Upload failed"); }
    finally { setBusy(false); if (inputRef.current) inputRef.current.value = ""; }
  };
  return (
    <>
      <Button variant="outline" size="sm" data-testid={`attach-${requirementId}`} onClick={() => inputRef.current?.click()} disabled={busy} className="rounded-lg border-dashed">
        {busy ? <Upload className="mr-1.5 h-3.5 w-3.5 animate-pulse" /> : <Paperclip className="mr-1.5 h-3.5 w-3.5" />} {busy ? "Uploading…" : "Attach sample"}
      </Button>
      <input ref={inputRef} type="file" multiple className="hidden" onChange={upload} />
    </>
  );
}

function RequirementDialog({ projectId, requirement, onSaved }) {
  const editing = !!requirement;
  const [label, setLabel] = useState(requirement?.label || "");
  const [description, setDescription] = useState(requirement?.description || "");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!label.trim()) { toast.error("Enter a document name"); return; }
    setBusy(true);
    try {
      if (editing) await api.patch(`/projects/${projectId}/doc-requirements/${requirement.id}`, { label: label.trim(), description: description.trim() });
      else await api.post(`/projects/${projectId}/doc-requirements`, { label: label.trim(), description: description.trim() });
      toast.success(editing ? "Updated" : "Document added");
      onSaved();
    } catch { toast.error("Could not save"); }
    finally { setBusy(false); }
  };
  return (
    <DialogContent>
      <DialogHeader><DialogTitle>{editing ? "Edit document row" : "Add a document to the checklist"}</DialogTitle></DialogHeader>
      <div className="space-y-3">
        <div><Label className="text-sm font-semibold">Document name</Label><Input data-testid="req-label" value={label} onChange={(e) => setLabel(e.target.value)} className="mt-1 rounded-lg" placeholder="e.g. Sample pricing / price list" /></div>
        <div><Label className="text-sm font-semibold">Description (optional)</Label><Textarea data-testid="req-description" value={description} onChange={(e) => setDescription(e.target.value)} className="mt-1 rounded-lg" placeholder="What should this file contain?" /></div>
      </div>
      <DialogFooter>
        <Button data-testid="req-save" onClick={save} disabled={busy} className="rounded-lg bg-sky-600 hover:bg-sky-700">{busy ? "Saving…" : editing ? "Save" : "Add"}</Button>
      </DialogFooter>
    </DialogContent>
  );
}
